
import { HttpClient, HttpEvent, HttpHeaderResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JobDigitalLogForm, JobDigitalLogFormData, JobDigitalLogOfflineRange, JobDigitalLogFormDataHistory, JobDigitalLogFormFieldValue, JobDigitalLogFormTimeSlot, JobDigitalLogFormTimeSlotData, JobDigitalLogFormFieldDoc, FieldDocumentListOrderItem } from '@vitralogy/ng-digital-log';
import { JobDigitalLogFormRequestTimeslotSave } from '@vitralogy/ng-digital-log/lib/models/job-digital-log-form-request-timeslot-save.model';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { DigitalLogSummary } from 'src/app/models/digital-log/digital-log-summary.model';

import { ConfigurationProfile, DigitalLogFormAlias, DigitalLogFormPhoto, DigitalLogLookupListInfo, JobDigitalLogFormRequest, JobDigitalLogOnDemandForm, JobDigitalLogOnDemandFormPriorAliases, DigitalLogProfileGroup, DigitalLogProfileGroupDetail, ResourceAttributeDefinition, StartMyWorkResult, BaseDigitalLogFormPhoto } from 'src/app/models/digital-log/digital-log.model';
import { FieldAttachment } from 'src/app/models/digital-log/field-attachment';
import { FieldAttachmentFilter } from 'src/app/models/digital-log/field-attachment-filter';
import { ReportContact } from 'src/app/models/report-contact';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root'
})
export class DigitalLogService {
	private baseDigitalLogUrl = `${environment.baseUrl}/digitallog`;

	constructor(private httpClient: HttpClient) { }

	@ApiMethod()
	getFormRequests(token: string): Observable<JobDigitalLogFormRequest[]> {
		return this.httpClient.get<JobDigitalLogFormRequest[]>(`${this.baseDigitalLogUrl}/${token}/requests`);
	}

	@ApiMethod()
	countOnDemandDLForms(token: string, isConfigurationForm: boolean): Observable<number> {
		return this.httpClient.get<number>(`${this.baseDigitalLogUrl}/${token}/requests-count`);
	}
	@ApiMethod()
	getActiveFormRequests(token: string): Observable<JobDigitalLogFormRequest[]> {
		return this.httpClient.get<JobDigitalLogFormRequest[]>(`${this.baseDigitalLogUrl}/${token}/requests-active`);
	}

	@ApiMethod()
	getForm(token: string, jobDigitalLogFormRequestId: number): Observable<JobDigitalLogForm> {
		return this.httpClient.get<JobDigitalLogForm>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/form`);
	}

	@ApiMethod()
	getFormPhotos(token: string, jobDigitalLogFormRequestId: number): Observable<DigitalLogFormPhoto[]> {
		return this.httpClient.get<DigitalLogFormPhoto[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/photos`);
	}

	@ApiMethod()
	saveFormPhoto(token: string, jobDigitalLogFormRequestId: number, photo: BaseDigitalLogFormPhoto): Observable<void> {
		return this.httpClient.put<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/photos`, photo);
	}

	@ApiMethod()
	deleteFormPhoto(token: string, jobDigitalLogFormRequestId: number, photoId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/photos/${photoId}`);
	}

	@ApiMethod()
	uploadFormPhotos(token: string, photos: File[], jobDigitalLogFormRequestId: number, isFieldLocked: boolean | null = null, note: string | null = null, digitalLogFormFieldId: number | null = null): Observable<any> {
		let observables: any[] = [];

		const chunkSize = 10;
		if (photos.length > chunkSize) {
			for (let i = 0; i < photos.length; i += chunkSize) {
				const photosChunk = photos.slice(i, i + chunkSize);
				observables.push(this.httpClient.post<any>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/photos`, this.getFormData(photosChunk, isFieldLocked, note, digitalLogFormFieldId)));
			}
		}
		else {
			observables.push(this.httpClient.post<any>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/photos`, this.getFormData(photos, isFieldLocked, note, digitalLogFormFieldId)));
		}

		return forkJoin(observables);
	}

	private getFormData(files: File[], isFieldLocked: boolean | null = null, note: string | null = null, digitalLogFormFieldId: number | null = null): FormData {
		const formData = new FormData();
		if (files) {
			files.forEach(file => formData.append('file[]', file, file.name));
		}

		if(isFieldLocked !== null){
			formData.append('isFieldLocked', JSON.stringify(isFieldLocked));
		}
		if(note !== null){
			formData.append('note', note);
		}
		if(digitalLogFormFieldId !== null){
			formData.append('digitalLogFormFieldId', JSON.stringify(digitalLogFormFieldId));
		}

		return formData;
	}

	@ApiMethod()
	getLookups(token: string, jobDigitalLogFormRequestId: number): Observable<DigitalLogLookupListInfo[]> {
		return this.httpClient.get<DigitalLogLookupListInfo[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/lookups`);
	}

	@ApiMethod()
	getData(token: string, jobDigitalLogFormRequestId: number): Observable<JobDigitalLogFormData[]> {
		return this.httpClient.get<JobDigitalLogFormData[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/data`);
	}

	@ApiMethod()
	saveData(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, formData: { staticData: JobDigitalLogFormFieldValue[], timeSlotsData: JobDigitalLogFormTimeSlotData[] }): Observable<void> {
		return this.httpClient.post<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/data`, formData);
	}

	@ApiMethod()
	saveTimeSlot(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, data: JobDigitalLogFormTimeSlot): Observable<JobDigitalLogFormTimeSlot> {
		return this.httpClient.post<JobDigitalLogFormTimeSlot>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/timeslot`, data);
	}
	@ApiMethod()
	saveTimeSlots(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, data: JobDigitalLogFormTimeSlot[]): Observable<JobDigitalLogFormTimeSlot[]> {
		return this.httpClient.post<JobDigitalLogFormTimeSlot[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/timeslots`, data);
	}
	@ApiMethod()
	getDataHistory(token: string, jobDigitalLogFormRequestId: number, digitalLogFormFieldId: number): Observable<JobDigitalLogFormDataHistory[]> {
		return this.httpClient.get<JobDigitalLogFormDataHistory[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/fields/${digitalLogFormFieldId}/history`);
	}

	@ApiMethod()
	saveDataHistory(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, historicalData: JobDigitalLogFormDataHistory): Observable<void> {
		return this.httpClient.post<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/fields/${historicalData.digitalLogFormFieldId}/history`, historicalData);
	}

	@ApiMethod()
	updateDataHistory(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, jobDigitalLogFormDataHistoryId: number, value: string): Observable<void> {
		const formData = new FormData();
		formData.append('value', value);
		return this.httpClient.put<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/history/${jobDigitalLogFormDataHistoryId}`, formData);
	}

	@ApiMethod()
	deleteDataHistory(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, jobDigitalLogFormDataHistoryId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/history/${jobDigitalLogFormDataHistoryId}`);
	}

	@ApiMethod()
	getFormCompletionTimeline(token: string, jobDigitalLogFormRequestId: number, year: number): Observable<any> {
		return this.httpClient.get<any>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/calendar/${year}`);
	}

	@ApiMethod()
	getOfflineRanges(token: string, jobDigitalLogFormRequestId: number): Observable<JobDigitalLogOfflineRange[]> {
		return this.httpClient.get<JobDigitalLogOfflineRange[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/offline-ranges`);
	}

	@ApiMethod()
	saveOfflineRange(token: string, jobDigitalLogFormRequestId: number, range: JobDigitalLogOfflineRange, ytdReportYears: number[]): Observable<number> {
		return this.httpClient.post<number>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/offline-ranges`, { range, ytdReportYears });
	}

	@ApiMethod()
	updateOfflineRange(token: string, jobDigitalLogFormRequestId: number, range: JobDigitalLogOfflineRange, ytdReportYears: number[]): Observable<void> {
		return this.httpClient.put<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/offline-ranges/${range.jobDigitalLogFormOfflineRangeId}`, { range, ytdReportYears });
	}

	@ApiMethod()
	deleteOfflineRange(token: string, jobDigitalLogFormRequestId: number, jobDigitalLogFormOfflineRangeId: number, ytdReportYears: number[]): Observable<HttpEvent<void>> {
		const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
		const request = new HttpRequest(
			'DELETE',
			`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/offline-ranges/${jobDigitalLogFormOfflineRangeId}`,
			ytdReportYears,
			{ headers }
		);
		return this.httpClient.request<void>(request);
	}

	@ApiMethod()
	setFormNotApplicable(token: string, jobDigitalLogFormRequestId: number, keepDataUpTo?: Date): Observable<void> {
		const formData = new FormData();
		formData.append('keepDataUpTo', keepDataUpTo ? keepDataUpTo?.toDateString() : null);
		return this.httpClient.post<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/not-applicable`, formData);
	}

	@ApiMethod()
	deleteOnDemandDLForm(token: string, jobDigitalLogFormRequestId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}`);
	}

	@ApiMethod()
	getOnDemandDLForms(token: string, isConfigurationForm: boolean): Observable<JobDigitalLogFormRequest[]> {
		return this.httpClient.get<JobDigitalLogFormRequest[]>(`${this.baseDigitalLogUrl}/${token}/on-demand-forms?cfg=${isConfigurationForm}`);
	}

	@ApiMethod()
	getOnDemandDLFormsPriorInspection(token: string, dlFormId: number | null = null): Observable<JobDigitalLogOnDemandFormPriorAliases[]> {
		let query: string = dlFormId ? `?dlFormId=${dlFormId}` : '';
		return this.httpClient.get<JobDigitalLogOnDemandFormPriorAliases[]>(`${this.baseDigitalLogUrl}/${token}/on-demand-forms/prior-inspection${query}`);
	}

	@ApiMethod()
	addOnDemandDigitalLogFormToJob(token: string, digitalLogOnDemandForms: JobDigitalLogOnDemandForm[]): Observable<number> {
		return this.httpClient.post<number>(`${this.baseDigitalLogUrl}/${token}/add-on-demand-form`, digitalLogOnDemandForms);
	}

	@ApiMethod()
	allowOnDemandFormRequest(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseDigitalLogUrl}/${token}/allow-on-demand-form-request`);
	}

	@ApiMethod()
	getDigitalLogFormAliases(token: string, onDemandForm: JobDigitalLogOnDemandForm): Observable<DigitalLogFormAlias[]> {
		return this.httpClient.get<DigitalLogFormAlias[]>(
			`${this.baseDigitalLogUrl}/${token}/${onDemandForm.digitalLogFormId}/${onDemandForm.accountSiteId}/${onDemandForm.accountSiteResourceId}/DL-form-aliases`);
	}

	@ApiMethod()
	saveAsset(token: string, alias: DigitalLogFormAlias, updateAttributes?: boolean): Observable<DigitalLogFormAlias> {
		let url = `${this.baseDigitalLogUrl}/${token}/asset`;
		if (updateAttributes === false) {
			url += '?updateAttributes=false';
		}
		return this.httpClient.post<DigitalLogFormAlias>(url, alias);
	}

	@ApiMethod()
	getOnDemandFormSummary(token: string, pageSize: number = 5, pageCount: number = 1): Observable<DigitalLogSummary> {
		return this.httpClient.get<DigitalLogSummary>(`${this.baseDigitalLogUrl}/${token}/requests-summary?pageSize=${pageSize}&pageCount=${pageCount}`);
	}

	@ApiMethod()
	generateNewReport(token: string, dates: Date[], contacts: ReportContact[]): Observable<any> {
		return this.httpClient.post<any>(`${this.baseDigitalLogUrl}/${token}/generate-reports`, { Dates: dates, Contacts: contacts });
	}

	@ApiMethod()
	public notifyBinderManagement(token: string, digitalLogFormId): Observable<void> {
		return this.httpClient.get<void>(`${this.baseDigitalLogUrl}/${token}/${digitalLogFormId}/notify-management`);
	}

	@ApiMethod()
	public getAdditionalRecipients(token: string): Observable<ReportContact[]> {
		return this.httpClient.get<ReportContact[]>(`${this.baseDigitalLogUrl}/${token}/report-contacts`);
	}

	@ApiMethod()
	completePendingOnDemandForms(token: string, formData: { staticData: JobDigitalLogFormFieldValue[], timeSlotsData: JobDigitalLogFormTimeSlotData[], jobDigitalLogFormRequestTimeslotIds: JobDigitalLogFormRequestTimeslotSave[], ytdReportYear: number }): Observable<void> {
		return this.httpClient.post<void>(`${this.baseDigitalLogUrl}/${token}/${formData.ytdReportYear}/data/assets`, formData);
	}

	@ApiMethod()
	getConfigurationProfile(token: string, groupId: number | null): Observable<ConfigurationProfile[]> {
		return this.httpClient.get<ConfigurationProfile[]>(`${this.baseDigitalLogUrl}/${token}/configuration-profile${groupId ? `/${groupId}` : ''}`);
	}

	@ApiMethod()
	updateConfigurationProfile(token: string, configuration: ConfigurationProfile[], groupId: number | null, groupName: string): Observable<number> {
		return this.httpClient.put<number>(`${this.baseDigitalLogUrl}/${token}/configuration-profile`,
			{ configuration, groupId, groupName });
	}

	@ApiMethod()
	startMyWork(token: string, profileGroupId?: number): Observable<StartMyWorkResult> {
		return this.httpClient.put<StartMyWorkResult>(`${this.baseDigitalLogUrl}/${token}/my-work/apply-profile`, { profileGroupId });
	}

	@ApiMethod()
	setMyWorkToToday(token: string, profileGroupId?: number): Observable<StartMyWorkResult> {
		return this.httpClient.put<StartMyWorkResult>(`${this.baseDigitalLogUrl}/${token}/my-work/set-to-today`, { profileGroupId });
	}

	@ApiMethod()
	consolidateMyWork(token: string, profileGroupId?: number): Observable<StartMyWorkResult> {
		return this.httpClient.put<StartMyWorkResult>(`${this.baseDigitalLogUrl}/${token}/my-work/consolidate-profile`, { profileGroupId });
	}

	@ApiMethod()
	clearRemainingWork(token: string): Observable<StartMyWorkResult> {
		return this.httpClient.put<StartMyWorkResult>(`${this.baseDigitalLogUrl}/${token}/my-work/clear-remaining`, {});
	}

	@ApiMethod()
	getFieldDocuments(token: string, jobDigitalLogFormRequestId: number, digitalLogFormFieldId: number = null): Observable<JobDigitalLogFormFieldDoc[]> {
		var url = `${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/field-documents`;
		if(!!digitalLogFormFieldId){
			url += `?digitalLogFormFieldId=${digitalLogFormFieldId}`;
		}
		return this.httpClient.get<JobDigitalLogFormFieldDoc[]>(url);
	}

	@ApiMethod()
	saveFieldDocument(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number,
		data: {
			digitalLogFormFieldId: number,
			documentClassAndTypeMappingId: number,
			file: File,
			timeSlot: JobDigitalLogFormTimeSlot,
			documentContainerId?: number,
			documentLabel?: string,
			description?: string,
			listOrder?: number,
			isDeprecated?: boolean
		}): Observable<{ fieldDocument: JobDigitalLogFormFieldDoc, jobDigitalLogFormTimeSlot: JobDigitalLogFormTimeSlot }> {
		const formData = this.getFormData([data.file]);
		formData.set("timeSlot", JSON.stringify(data.timeSlot));
		formData.append('documentContainerId', JSON.stringify(data.documentContainerId));
		formData.append('documentLabel', data.documentLabel);
		formData.append('description', data.description);
		formData.append('listOrder', JSON.stringify(data.listOrder));
		formData.append('isDeprecated', JSON.stringify(data.isDeprecated));
		return this.httpClient.post<{ fieldDocument: JobDigitalLogFormFieldDoc, jobDigitalLogFormTimeSlot: JobDigitalLogFormTimeSlot }>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/field-documents/upload/${data.digitalLogFormFieldId}/doc-type/${data.documentClassAndTypeMappingId}`, formData);
	}

	@ApiMethod()
	updateFieldDocument(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number,
		data: {
			digitalLogFormFieldId: number,
			documentClassAndTypeMappingId: number,
			timeSlot: JobDigitalLogFormTimeSlot,
			documentContainerId?: number,
			documentLabel?: string,
			description?: string,
			listOrder?: number,
			isDeprecated?: boolean,
			documentId: number
		}): Observable<{ fieldDocument: JobDigitalLogFormFieldDoc, jobDigitalLogFormTimeSlot: JobDigitalLogFormTimeSlot }> {
		const formData  = new FormData();
		formData.set('timeSlot', JSON.stringify(data.timeSlot));
		formData.append('documentContainerId', JSON.stringify(data.documentContainerId));
		formData.append('documentLabel', data.documentLabel);
		formData.append('description', data.description);
		formData.append('listOrder', JSON.stringify(data.listOrder));
		formData.append('isDeprecated', JSON.stringify(data.isDeprecated));
		formData.append('documentId', JSON.stringify(data.documentId));
		return this.httpClient.put<{ fieldDocument: JobDigitalLogFormFieldDoc, jobDigitalLogFormTimeSlot: JobDigitalLogFormTimeSlot }>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/field-documents/update/${data.digitalLogFormFieldId}/doc-type/${data.documentClassAndTypeMappingId}`, formData);
	}

	@ApiMethod()
	deleteFieldDocument(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, digitalLogFormFieldId: number, jobDigitalLogFormFieldDocId: number, jobDigitalLogFormTimeSlotId: number): Observable<DigitalLogFormPhoto[]> {
		return this.httpClient.delete<JobDigitalLogFormFieldDoc[]>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/field-documents/${digitalLogFormFieldId}/${jobDigitalLogFormFieldDocId}/${jobDigitalLogFormTimeSlotId}`);
	}

	@ApiMethod()
	updateFieldDocumentListOrder(token: string, jobDigitalLogFormRequestId: number, ytdReportYear: number, items: FieldDocumentListOrderItem[], digitalLogFormFieldId: number): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.baseDigitalLogUrl}/${token}/${jobDigitalLogFormRequestId}/${ytdReportYear}/field-documents/${digitalLogFormFieldId}/update-list-order`, items);
	}

	@ApiMethod()
	getFieldsAttachments(token, filter: FieldAttachmentFilter): Observable<FieldAttachment[]> {
		return this.httpClient.post<FieldAttachment[]>(`${this.baseDigitalLogUrl}/${token}/fields-attachments`, filter);
	}

	@ApiMethod()
	updateFieldAttachment(token, field: FieldAttachment): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.baseDigitalLogUrl}/${token}/fields-attachments/update`, field);
	}

	@ApiMethod()
	getProfileGroups(token: string): Observable<DigitalLogProfileGroupDetail[]> {
		return this.httpClient.get<DigitalLogProfileGroupDetail[]>(`${this.baseDigitalLogUrl}/${token}/configuration-profile-groups`);
	}
	@ApiMethod()
	deleteProfileGroup(token: string, digitalLogProfileGroupId: number) {
		return this.httpClient.delete<boolean>(`${this.baseDigitalLogUrl}/${token}/configuration-profile-groups/${digitalLogProfileGroupId}`);
	}
	@ApiMethod()
	updateProfileGroupName(token: string, group: DigitalLogProfileGroup): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.baseDigitalLogUrl}/${token}/configuration-profile-groups/${group.digitalLogProfileGroupId}`,
			{ groupId: group.digitalLogProfileGroupId, groupName: group.name });
	}
	@ApiMethod()
	createProfileGroup(token: string, group: DigitalLogProfileGroup): Observable<number> {
		return this.httpClient.post<number>(`${this.baseDigitalLogUrl}/${token}/configuration-profile-groups`,
			{ groupName: group.name });
	}
	@ApiMethod()
	getResourceAttributeDefinitions(token: string, resourceTypeId: number, allowPrivateAttributes: boolean = false, allowPricingCatalogAttributes: boolean = false): Observable<ResourceAttributeDefinition[]> {
		return this.httpClient.get<ResourceAttributeDefinition[]>(
			`${this.baseDigitalLogUrl}/${token}/${resourceTypeId}/attribute-definitions?allowPrivateAttributes=${allowPrivateAttributes}&allowPricingCatalogAttributes=${allowPricingCatalogAttributes}`
		);
	}
}
